export const PRICE = '19 USD'
export const REGULAR_VALUE = '$97'

export const BRAND = 'Nail Educator Ready™'

export const heroBenefits = [
  {
    label: '150 Ready-to-Teach Lessons',
    description: 'Objective, demonstration, practice, corrections and assessment — already structured.',
  },
  {
    label: '6 Complete Volumes',
    description: 'From nail science and safety to gel, acrylic, e-file and troubleshooting.',
  },
  {
    label: '5 Practical Bonuses',
    description: 'Assessments, 100 mistake corrections, 75 drills, visual guide and 50 workshops.',
  },
]

/** Section 2 — what educators figure out before every class. */
export const painPoints = [
  { emoji: '📋', text: 'What should I teach first?' },
  { emoji: '💬', text: 'How should I explain this technique?' },
  { emoji: '🖐️', text: 'What should I demonstrate?' },
  { emoji: '🔍', text: 'What mistakes should I watch for?' },
  { emoji: '🛠️', text: 'How do I correct a student who keeps getting it wrong?' },
  { emoji: '✅', text: "How do I know when they're ready to move forward?" },
]

export const teachingFlow = [
  'Open',
  'Teach',
  'Demonstrate',
  'Practice',
  'Correct',
  'Assess',
]

/** Section 3 — what the system lets you do, step by step. */
export const transformationUses = [
  'Introduce the learning objective',
  'Explain the essential theory',
  'Guide your demonstration',
  'Give students structured practice',
  'Identify common technical mistakes',
  'Explain why those mistakes happen',
  'Correct the technique',
  'Evaluate the final result',
  'Track student progress',
]

export const guides = [
  {
    number: '01',
    title: 'Nail Science, Safety & Professional Foundations',
    description:
      'Anatomy, nail health, product chemistry, hygiene, infection control, allergies, client consultation and professional safety.',
    accent: '#EFE3D1',
  },
  {
    number: '02',
    title: 'Professional Manicure & Natural Nail Mastery',
    description:
      'Nail preparation, cuticle care, filing, shapes, polish application, French manicure and professional service techniques.',
    accent: '#C3D8EC',
  },
  {
    number: '03',
    title: 'Gel Systems & Structured Manicure Mastery',
    description:
      'Gel polish, builder gel, BIAB-style systems, overlays, apex placement, nail architecture, curing and maintenance.',
    accent: '#F2C9D2',
  },
  {
    number: '04',
    title: 'Acrylic, Extensions & Nail Architecture',
    description:
      'Liquid & powder acrylic, tips, forms, sculpted and soft gel extensions, product control, C-curve, rebalancing and removal.',
    accent: '#C9DCC0',
  },
  {
    number: '05',
    title: 'Advanced Nail Techniques, E-File & Troubleshooting',
    description:
      'Electric filing, bit selection, advanced shapes, structure correction, lifting, cracking, repairs and finishing.',
    accent: '#D9CFEF',
  },
  {
    number: '06',
    title: 'Nail Educator Teaching & Assessment System',
    description:
      'Lesson planning, demonstrations, student practice, error correction, skills checklists, rubrics and progress tracking.',
    accent: '#F3D79C',
  },
]

/** Section 5 — real interior pages, ordered by file name (01 → 05). */
export const insidePages = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/x3hn6qDtQpr6JtmwAQp1Un/carousel-01_27.webp',
    caption: 'Page 27 — Apex Placement',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/9NEt5bEX3dvu8bL4nRv9Ex/carousel-02_10.webp',
    caption: 'Page 10 — Working Angles',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ha8ezjk34tq3x47j93jaTW/carousel-03_25.webp',
    caption: 'Page 25 — Shape Correction',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/8EhyAUw7WWDgc3GEirsWig/carousel-04_32.webp',
    caption: 'Page 32 — Lifting Is a Clue',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/r3o7H83cX8owPpvW6n663U/carousel-05_49.webp',
    caption: 'Page 49 — Advanced Student Skills Assessment',
  },
]

/** Section 6 — the ten parts of every lesson. */
export const lessonStructure = [
  { number: '01', title: 'Learning Objective', description: 'Know exactly what students should understand or perform.' },
  { number: '02', title: 'Instructor Preparation', description: 'Know what to prepare before teaching.' },
  { number: '03', title: 'Theory', description: 'Explain the essential concepts without unnecessary filler.' },
  { number: '04', title: 'Instructor Demonstration', description: 'Structure what you need to show.' },
  { number: '05', title: 'Step-by-Step Procedure', description: 'Break techniques into teachable stages.' },
  { number: '06', title: 'Student Practice', description: 'Move students from watching to doing.' },
  { number: '07', title: 'Common Mistakes', description: 'Know what to watch for while students work.' },
  { number: '08', title: 'Instructor Corrections', description: 'Know how to respond when the technique goes wrong.' },
  { number: '09', title: 'Assessment', description: 'Check whether the student has actually developed the skill.' },
  { number: '10', title: 'Practice Assignment', description: 'Reinforce the technique through repetition.' },
]

/** Section 7 — before and after. */
export const withoutSystem = [
  'Blank pages',
  'Scattered notes',
  'Last-minute preparation',
  'Repeated explanations',
  'Inconsistent assessments',
  'Creating new exercises every class',
]

export const withSystem = [
  'Structured lessons',
  'Teaching sequences',
  'Visual references',
  'Practice exercises',
  'Correction frameworks',
  'Assessment tools',
  'Progress tracking',
]

/** Section 8 — who it is built for. */
export const audience = [
  'Teach beginner or intermediate nail students',
  'Offer private 1:1 nail training',
  'Run group nail classes',
  'Own or teach inside a nail academy',
  'Host nail workshops or intensives',
  'Are an experienced Nail Technician moving into education',
  'Know your technique but spend too much time preparing materials',
  'Want your classes to feel more structured and professional',
]

/** Section 9 — the five bonuses. */
export const bonusDetails = [
  {
    number: 'Bonus #1',
    title: 'Student Assessment & Practical Evaluation System',
    hook: 'Grade any practical in minutes — fairly and consistently.',
    description:
      'Skills checklists, scoring criteria and evaluation guides for technique, safety, structure, product control and finish. No more guessing whether a student is ready.',
    accent: '#F2C9D2',
  },
  {
    number: 'Bonus #2',
    title: '100 Student Mistakes & Instructor Corrections',
    hook: 'Know exactly what to say when a student gets it wrong.',
    description:
      'Every mistake broken down so you fix the technique — not just the result.',
    flow: 'Problem → Cause → Error → Correction → Practice',
    accent: '#C3D8EC',
  },
  {
    number: 'Bonus #3',
    title: '75 Demonstrations & Student Practice Drills',
    hook: 'Never run out of hands-on practice again.',
    description:
      'Focused drills that build real technical control through repetition — from foundation skills to advanced work.',
    accent: '#C9DCC0',
  },
  {
    number: 'Bonus #4',
    title: 'Nail Educator Visual Reference Guide',
    hook: 'Show it once — and students finally get it.',
    description:
      'Shapes, architecture, apex placement, forms and e-file bits in clear classroom visuals. Show it. Explain it. Demonstrate it.',
    accent: '#D9CFEF',
  },
  {
    number: 'Bonus #5',
    title: '50 Ready-to-Teach Workshops & Intensives',
    hook: 'Launch a new workshop without writing a single plan.',
    description:
      'Short-class structures for private sessions, focused trainings and intensives — each one ending in a final challenge.',
    flow: 'Objective → Theory → Demo → Practice → Correction → Challenge',
    accent: '#F3D79C',
  },
]

/** Section 10 — everything included. */
export const offerIncludes = [
  '6 Complete Nail Educator Volumes',
  '150 Ready-to-Teach Nail Lessons',
  'Nail Science & Safety Training',
  'Professional Manicure Training',
  'Gel & Structured Manicure Training',
  'Acrylic & Extension Training',
  'E-File & Advanced Technique Training',
  'Troubleshooting Resources',
  'Instructor Correction Frameworks',
  'Student Practice Activities',
  'Practical Assessment Resources',
  'Student Skills Checklists',
  'Visual Teaching References',
  '100 Student Mistakes & Corrections',
  '75 Practice Drills',
  '50 Workshop Structures',
  'Digital PDF Format',
  'Instant Access',
]

/** Section 11 — what it would take to build yourself. */
export const valueItems = [
  '150 lesson structures',
  'Student exercises',
  'Technique explanations',
  'Correction guides',
  'Assessments',
  'Visual references',
  'Practice drills',
  'Workshop structures',
]

/** Section 12 — what happens after purchase. */
export const afterPurchase = [
  {
    number: '1',
    title: 'Get Instant Access',
    description: 'Complete your purchase and access your digital Nail Educator Ready™ resources.',
  },
  {
    number: '2',
    title: "Choose What You're Teaching",
    description: 'Select the relevant volume, lesson, demonstration, drill or assessment.',
  },
  {
    number: '3',
    title: 'Prepare Your Class',
    description: 'Review the teaching objective, demonstration and correction points.',
  },
  {
    number: '4',
    title: 'Teach',
    description:
      'Use the material alongside your own expertise and professional product-system instructions.',
  },
  {
    number: '5',
    title: 'Practice & Correct',
    description: 'Give students structured practice and identify technical errors.',
  },
  {
    number: '6',
    title: 'Assess',
    description: 'Use the included criteria and resources to evaluate progress.',
  },
]

export const faqs = [
  {
    question: 'Is Nail Educator Ready™ for nail students?',
    answer:
      'The primary audience is Nail Educators, Nail Instructors, Trainers and Academy Owners. The resources are structured to help educators teach, demonstrate, correct and assess nail students.',
  },
  {
    question: 'Do I need to be an experienced educator?',
    answer:
      'No. The system can be particularly useful if you are an experienced Nail Technician beginning to teach, because it provides structure for organizing lessons and student practice. You should still teach only techniques within your professional competence and local requirements.',
  },
  {
    question: 'Is this a certification program?',
    answer:
      'No. Nail Educator Ready™ is a digital teaching-resource system. It does not provide professional licensing, accreditation or government-recognized certification.',
  },
  {
    question: 'What format are the resources?',
    answer:
      'The resources are provided as digital PDF materials designed for convenient reference and use during lesson preparation and teaching.',
  },
  {
    question: 'Are the files editable?',
    answer:
      'No. The resources are delivered as finished PDFs rather than editable Word or Canva templates.',
  },
  {
    question: 'Can I use the materials with my students?',
    answer:
      'They are designed to support your teaching. Your purchase does not automatically grant redistribution or resale rights; the applicable product license should specify permitted classroom use.',
  },
  {
    question: 'Does the system replace manufacturer instructions?',
    answer:
      'No. Always follow the instructions, compatibility requirements and safety guidance provided by the manufacturers of the professional products and equipment you use.',
  },
  {
    question: 'Does it cover E-File and advanced techniques?',
    answer:
      'Yes. Volume 5 includes e-file concepts, bit selection, advanced shapes, nail architecture, troubleshooting, repairs, rebalancing and finishing.',
  },
  {
    question: 'Is this a physical product?',
    answer:
      'No. Nail Educator Ready™ is a digital product. Nothing will be shipped.',
  },
  {
    question: 'How much does it cost?',
    answer: 'You can get the complete system for a one-time payment of $19 USD.',
  },
]
